/**
 * Copy of the AMS View grid and trend kit (`components/Ams/view/{grid,trend}`).
 * Spread into `en.amsView`; owns the `metric`, `grid`, `trend` and `totals`
 * sub-keys. VentaHub's English, verbatim where VentaHub had it.
 */
export const amsViewKit = {
  // pages/ams_view/components/metricInfo.ts:40-181 — the label and the
  // one-line definition every tooltip on the page shows.
  metric: {
    impressions: {
      label: 'Impressions',
      description: 'Total number of times the ad was shown to shoppers in the selected period.',
    },
    clicks: {
      label: 'Clicks',
      description: 'Total number of times shoppers clicked the ad.',
    },
    ctr: {
      label: 'CTR',
      description:
        'Click-through rate — Clicks ÷ Impressions × 100. Higher means the ad is more compelling.',
    },
    spend: {
      label: 'Spend',
      description: 'Total amount spent on this ad group in the period.',
    },
    cpc: {
      label: 'CPC',
      description: 'Average cost per click — Spend ÷ Clicks. Lower means cheaper traffic.',
    },
    units: {
      label: 'Units',
      description: 'Units sold attributed to ads in the period.',
    },
    sales: {
      label: 'Sales',
      description: 'Revenue from orders attributed to ads in the period.',
    },
    asp: {
      label: 'ASP',
      description:
        'Average selling price — Sales ÷ Units. Tracks the price mix of what the ads actually sold.',
    },
    acos: {
      label: 'ACOS',
      description:
        'Advertising Cost of Sales — Spend ÷ Sales × 100. Lower is better; profitable below your margin rate.',
    },
    roas: {
      label: 'ROAS',
      description:
        'Return on Ad Spend — Sales ÷ Spend. Higher is better; > 1× means revenue exceeds spend.',
    },
    conversion: {
      label: 'Conv. rate',
      description:
        'Conversion rate — Units ÷ Clicks × 100. Indicates how often a click turns into a sale.',
    },
    targets: {
      label: 'Targets',
      description: 'Number of keyword / product targets in this ad group.',
    },
    asins: {
      label: 'ASINs',
      description: 'Number of ASINs advertised in this ad group.',
    },
    searchTerms: {
      label: 'Search Terms',
      description: 'Number of customer search terms that matched this ad group.',
    },
    negatives: {
      label: 'Negatives',
      description: 'Number of negative keywords / negative targets attached to this ad group.',
    },
    bid: {
      label: 'Bid',
      description: 'The per-click bid configured on this target. N/A when Amazon reports none.',
    },
    spendShare: {
      label: 'Spend share',
      description: "This row's share of the ad group's spend in the period.",
    },
    salesShare: {
      label: 'Sales share',
      description: "This row's share of the ad group's ad-attributed sales in the period.",
    },
    clickShare: {
      label: 'Click share',
      description: "This row's share of the ad group's clicks in the period.",
    },
    wastedSpend: {
      label: 'Spend without sales',
      description:
        'Spend on a row that produced no attributed sales in the period — the usual trigger for a negative keyword. Blank when the row made any sale at all, because that is a bid problem rather than a wasted one.',
    },
    acosGap: {
      label: 'ACOS vs target',
      description:
        "Measured ACOS minus the ad group's target ACOS, in percentage points. Negative is better.",
    },
    cvrIndex: {
      label: 'CVR index',
      description:
        "Conversion rate relative to the ad group's. 1.00x is the same rate; above 1 converts better than its peers.",
    },
    roasIndex: {
      label: 'ROAS index',
      description:
        "Return on ad spend relative to the ad group's. 1.00x is the same return; below 1 earns less per unit of spend than its peers.",
    },
  },
  grid: {
    // metricColumns.ts:68-210 — the header of each shared metric column.
    columns: {
      impressions: 'Impr.',
      clicks: 'Clicks',
      ctr: 'CTR',
      spend: 'Spend',
      cpc: 'CPC',
      units: 'Units',
      sales: 'Sales',
      asp: 'ASP',
      acos: 'ACOS',
      roas: 'ROAS',
      conversion: 'Conv.',
    },
    // MetricTable.tsx:199-202, 316, 441
    search: 'Search rows',
    searchPlaceholder: 'Search…',
    empty: 'No rows match the current filters.',
    rowsPerPage: 'Rows per page',
    expandColumn: 'Details',
    expand: 'Expand {{name}}',
    // AdGroupTable.tsx:460-490 — an enabled ad group whose campaign is stopped.
    halted: {
      paused: 'Campaign paused',
      archived: 'Campaign archived',
      tooltip:
        'Ad group is {{status}}, but its campaign is {{campaignStatus}} — nothing is serving.',
    },
  },
  // MetricTable.tsx:490-493 — the totals line under every grid.
  totals: {
    count: 'Total: {{total}}',
    countWithCurrency: 'Total: {{total}} · {{currency}}',
  },
  // TrendChart.tsx:141-196, 564-855 — the chart inside an expanded row.
  trend: {
    title: 'Trend',
    metrics: 'Chart metrics: {{metrics}}',
    groups: {
      currency: 'Currency · same Y-axis',
      count: 'Counts · same Y-axis',
    },
    granularities: {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
    },
    customize: 'Customize',
    customizeLabel: 'Customize chart',
    overridden: 'Chart dates overridden',
    granularity: 'Chart granularity',
    range: 'Chart dates',
    reset: 'Reset to page dates',
    loading: 'Loading…',
    loadingSome: 'Loading {{loaded}} / {{total}}',
    empty: 'No data for the selected range.',
    // TrendChart.tsx:224-270, the chips under each granularity.
    presets: {
      lastDays_one: 'Last {{count}} day',
      lastDays_other: 'Last {{count}} days',
      lastWeeks_one: 'Last {{count}} week',
      lastWeeks_other: 'Last {{count}} weeks',
      lastMonths_one: 'Last {{count}} month',
      lastMonths_other: 'Last {{count}} months',
      lastQuarters_one: 'Last {{count}} quarter',
      lastQuarters_other: 'Last {{count}} quarters',
      lastYears_one: 'Last {{count}} year',
      lastYears_other: 'Last {{count}} years',
      thisMonth: 'This month',
      thisQuarter: 'This quarter',
      thisYear: 'This year',
      yearToDate: 'Year to date',
    },
  },
} as const
